import { createContext, useContext, type ReactNode } from 'react'

/**
 * What the shell's sidebar says about a right - `FRONTEND.md` §9.752 (design §2.9, *Na obrazovke*).
 *
 * The roles editor draws each right with **the screen it opens**, so a main administrator ticking *Faktúry* sees the
 * sidebar entry it puts in somebody's menu. That catalogue is the shell's: the sidebar is composed in `apps/manager`
 * from the generated nav tree and AGOS's own entries, and a feature module cannot import the app. So the shell lends it
 * through this context, and a screen outside the shell - a test, a module rendered alone - reads the empty default and
 * draws no screen rather than a wrong one.
 *
 * Only the catalogue is lent today. The rail's *Čo uvidí v menu* (the sidebar evaluated against a proposed set) is
 * deferred by §9.752 and would extend this value; a reader of `screensOf` is unaffected by it.
 */
export interface MenuPreview {
  /** The sidebar entries a right shows, by their source labels (render each through `<T>`); none when unknown. */
  screensOf: (permission: string) => readonly string[]
}

const NO_SCREENS: readonly string[] = []

/** What a screen reads outside the shell: no catalogue, so no screen is named. */
export const EMPTY_MENU_PREVIEW: MenuPreview = { screensOf: () => NO_SCREENS }

const MenuPreviewContext = createContext<MenuPreview>(EMPTY_MENU_PREVIEW)

export function MenuPreviewProvider({ value, children }: { value: MenuPreview; children: ReactNode }) {
  return <MenuPreviewContext.Provider value={value}>{children}</MenuPreviewContext.Provider>
}

export function useMenuPreview(): MenuPreview {
  return useContext(MenuPreviewContext)
}
